/**
 * The document of the cover editor (spec `2026-10-08-music24-cover-editor-design.md` §3): a
 * picture or a colour, text boxes and effects. All positions and sizes are shares of the cover
 * side (0..1), so a document draws at any size. The web editor, the public tool and music24-api
 * use this one contract. The file has no heavy imports.
 */
import { isHexColour } from './kitPalette';
import {
  contrastRatio,
  KIT_FONT_FACES,
  KIT_FONT_IDS,
  RELEASE_THEME_IDS,
  themeType,
  type KitFontId,
  type ReleaseThemeId,
} from './releaseThemes';

export const COVER_DOC_VERSION = 1;
/** The side of the exported cover in pixels: the size that distributors ask for. */
export const COVER_EXPORT_PX = 3000;

export type CoverPictureSource =
  /** A `CoverAiPicture` of the account. */
  | { kind: 'ai'; pictureUuid: string }
  /** A published `CoverBackground` of the bank. */
  | { kind: 'bank'; backgroundUuid: string }
  /** A file of the account. `fileId` is null in the public tool: the picture stays in the browser. */
  | { kind: 'upload'; fileId: number | null };

export type CoverBackground =
  | {
      kind: 'picture';
      source: CoverPictureSource;
      url: string;
      /** The point of the picture that stays in view (0..1), and the zoom (1 = the picture covers the square). */
      focusX: number;
      focusY: number;
      zoom: number;
    }
  | { kind: 'colour'; colour: string }
  | { kind: 'gradient'; from: string; to: string; angleDeg: number };

export const COVER_TEXT_ROLES = ['title', 'artist', 'free'] as const;
export type CoverTextRole = (typeof COVER_TEXT_ROLES)[number];
export const COVER_TEXT_ALIGNS = ['left', 'center', 'right'] as const;
export type CoverTextAlign = (typeof COVER_TEXT_ALIGNS)[number];
export const COVER_TEXT_CASES = ['none', 'upper', 'lower'] as const;
export type CoverTextCase = (typeof COVER_TEXT_CASES)[number];

export interface CoverTextShadow {
  colour: string;
  blurShare: number;
  offsetXShare: number;
  offsetYShare: number;
}

export interface CoverTextBox {
  id: string;
  role: CoverTextRole;
  text: string;
  fontId: KitFontId;
  /** Inside `KIT_FONT_FACES[fontId].weights`. */
  weight: number;
  italic: boolean;
  /** The font size as a share of the cover side. */
  sizeShare: number;
  colour: string;
  opacity: number;
  align: CoverTextAlign;
  textCase: CoverTextCase;
  /** Letter spacing in em. */
  tracking: number;
  lineHeight: number;
  /** The centre of the box. */
  x: number;
  y: number;
  /** The wrap width. */
  width: number;
  rotationDeg: number;
  shadow: CoverTextShadow | null;
  outline: { colour: string; widthShare: number } | null;
  glow: { colour: string; blurShare: number } | null;
}

export const COVER_EFFECT_TYPES = [
  'grain',
  'vignette',
  'circles',
  'neonFrame',
  'lightLeak',
  'halftone',
  'duotone',
  'blur',
] as const;
export type CoverEffectType = (typeof COVER_EFFECT_TYPES)[number];
export const COVER_EFFECT_LAYERS = ['underText', 'overText'] as const;
export type CoverEffectLayer = (typeof COVER_EFFECT_LAYERS)[number];

export interface CoverEffect {
  id: string;
  type: CoverEffectType;
  /** 0..1 */
  strength: number;
  /** The colours of an effect that has them (neon frame, circles, light leak, duotone). */
  colours: string[];
  /** Under or over the text boxes. */
  layer: CoverEffectLayer;
}

export interface CoverDoc {
  version: typeof COVER_DOC_VERSION;
  background: CoverBackground;
  /** In draw order: the last box is on top. */
  texts: CoverTextBox[];
  /** In draw order inside each layer. */
  effects: CoverEffect[];
  /** The 5 colours of the picture (or of the theme): the colour swatches of the editor. */
  palette: string[];
  /** The release theme that the defaults came from. */
  themeId: ReleaseThemeId;
}

export const COVER_DOC_LIMITS = {
  texts: 12,
  textLength: 400,
  effects: 8,
  effectColours: 4,
  palette: 5,
  urlLength: 2048,
  /** The largest JSON text of a document that the API stores. */
  jsonBytes: 64 * 1024,
  sizeShare: [0.01, 0.6],
  width: [0.05, 1.5],
  zoom: [1, 4],
  tracking: [-0.1, 1],
  lineHeight: [0.7, 3],
} as const;

/** How many colours each effect uses, and where it sits by default. */
export const COVER_EFFECT_DEFAULTS: Readonly<
  Record<CoverEffectType, { strength: number; colourCount: number; layer: CoverEffectLayer }>
> = {
  grain: { strength: 0.35, colourCount: 0, layer: 'overText' },
  vignette: { strength: 0.5, colourCount: 0, layer: 'underText' },
  circles: { strength: 0.5, colourCount: 2, layer: 'underText' },
  neonFrame: { strength: 0.6, colourCount: 1, layer: 'underText' },
  lightLeak: { strength: 0.5, colourCount: 2, layer: 'underText' },
  halftone: { strength: 0.4, colourCount: 0, layer: 'underText' },
  duotone: { strength: 0.8, colourCount: 2, layer: 'underText' },
  blur: { strength: 0.3, colourCount: 0, layer: 'underText' },
};

const clamp = (value: number, min: number, max: number): number => Math.min(max, Math.max(min, value));

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function numberOf(value: unknown, fallback: number, min: number, max: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? clamp(value, min, max) : fallback;
}

function colourOf(value: unknown, fallback: string): string {
  return isHexColour(value) ? value.toLowerCase() : fallback;
}

function oneOf<T extends string>(value: unknown, allowed: readonly T[], fallback: T): T {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value) ? (value as T) : fallback;
}

/** An id of a box or an effect: letters, digits, `-` and `_`, at most 40 characters. */
const ID_PATTERN = /^[A-Za-z0-9_-]{1,40}$/;
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The weight of a font, inside the range of its file. Pure. */
export function clampCoverFontWeight(fontId: KitFontId, weight: number): number {
  const [min, max] = KIT_FONT_FACES[fontId].weights;
  return clamp(Math.round(weight), min, max);
}

function parseSource(value: unknown): CoverPictureSource | null {
  if (!isRecord(value)) {
    return null;
  }
  if (value.kind === 'ai' && typeof value.pictureUuid === 'string' && UUID_PATTERN.test(value.pictureUuid)) {
    return { kind: 'ai', pictureUuid: value.pictureUuid };
  }
  if (value.kind === 'bank' && typeof value.backgroundUuid === 'string' && UUID_PATTERN.test(value.backgroundUuid)) {
    return { kind: 'bank', backgroundUuid: value.backgroundUuid };
  }
  if (value.kind === 'upload') {
    const fileId = typeof value.fileId === 'number' && Number.isInteger(value.fileId) && value.fileId > 0;
    return { kind: 'upload', fileId: fileId ? (value.fileId as number) : null };
  }
  return null;
}

function parseBackground(value: unknown): CoverBackground | null {
  if (!isRecord(value)) {
    return null;
  }
  if (value.kind === 'colour') {
    return isHexColour(value.colour) ? { kind: 'colour', colour: value.colour.toLowerCase() } : null;
  }
  if (value.kind === 'gradient') {
    return isHexColour(value.from) && isHexColour(value.to)
      ? {
          kind: 'gradient',
          from: value.from.toLowerCase(),
          to: value.to.toLowerCase(),
          angleDeg: numberOf(value.angleDeg, 135, 0, 360),
        }
      : null;
  }
  if (value.kind === 'picture') {
    const source = parseSource(value.source);
    const { url } = value;
    if (source === null || typeof url !== 'string' || url === '' || url.length > COVER_DOC_LIMITS.urlLength) {
      return null;
    }
    return {
      kind: 'picture',
      source,
      url,
      focusX: numberOf(value.focusX, 0.5, 0, 1),
      focusY: numberOf(value.focusY, 0.5, 0, 1),
      zoom: numberOf(value.zoom, 1, COVER_DOC_LIMITS.zoom[0], COVER_DOC_LIMITS.zoom[1]),
    };
  }
  return null;
}

function parseText(value: unknown): CoverTextBox | null {
  if (!isRecord(value) || typeof value.id !== 'string' || !ID_PATTERN.test(value.id)) {
    return null;
  }
  if (typeof value.text !== 'string') {
    return null;
  }
  const fontId = oneOf(value.fontId, KIT_FONT_IDS, 'grotesk');
  const shadow = isRecord(value.shadow)
    ? {
        colour: colourOf(value.shadow.colour, '#000000'),
        blurShare: numberOf(value.shadow.blurShare, 0.01, 0, 0.2),
        offsetXShare: numberOf(value.shadow.offsetXShare, 0, -0.1, 0.1),
        offsetYShare: numberOf(value.shadow.offsetYShare, 0, -0.1, 0.1),
      }
    : null;
  const outline = isRecord(value.outline)
    ? {
        colour: colourOf(value.outline.colour, '#000000'),
        widthShare: numberOf(value.outline.widthShare, 0.002, 0, 0.05),
      }
    : null;
  const glow = isRecord(value.glow)
    ? { colour: colourOf(value.glow.colour, '#ffffff'), blurShare: numberOf(value.glow.blurShare, 0.02, 0, 0.2) }
    : null;
  return {
    id: value.id,
    role: oneOf(value.role, COVER_TEXT_ROLES, 'free'),
    text: value.text.slice(0, COVER_DOC_LIMITS.textLength),
    fontId,
    weight: clampCoverFontWeight(fontId, numberOf(value.weight, KIT_FONT_FACES[fontId].displayWeight, 1, 1000)),
    italic: value.italic === true,
    sizeShare: numberOf(value.sizeShare, 0.08, COVER_DOC_LIMITS.sizeShare[0], COVER_DOC_LIMITS.sizeShare[1]),
    colour: colourOf(value.colour, '#ffffff'),
    opacity: numberOf(value.opacity, 1, 0, 1),
    align: oneOf(value.align, COVER_TEXT_ALIGNS, 'center'),
    textCase: oneOf(value.textCase, COVER_TEXT_CASES, 'none'),
    tracking: numberOf(value.tracking, 0, COVER_DOC_LIMITS.tracking[0], COVER_DOC_LIMITS.tracking[1]),
    lineHeight: numberOf(value.lineHeight, 1.1, COVER_DOC_LIMITS.lineHeight[0], COVER_DOC_LIMITS.lineHeight[1]),
    x: numberOf(value.x, 0.5, -0.5, 1.5),
    y: numberOf(value.y, 0.5, -0.5, 1.5),
    width: numberOf(value.width, 0.8, COVER_DOC_LIMITS.width[0], COVER_DOC_LIMITS.width[1]),
    rotationDeg: numberOf(value.rotationDeg, 0, -180, 180),
    shadow,
    outline,
    glow,
  };
}

function parseEffect(value: unknown): CoverEffect | null {
  if (!isRecord(value) || typeof value.id !== 'string' || !ID_PATTERN.test(value.id)) {
    return null;
  }
  if (typeof value.type !== 'string' || !(COVER_EFFECT_TYPES as readonly string[]).includes(value.type)) {
    return null;
  }
  const type = value.type as CoverEffectType;
  const defaults = COVER_EFFECT_DEFAULTS[type];
  const colours = Array.isArray(value.colours)
    ? value.colours
        .filter(isHexColour)
        .slice(0, COVER_DOC_LIMITS.effectColours)
        .map((colour) => colour.toLowerCase())
    : [];
  return {
    id: value.id,
    type,
    strength: numberOf(value.strength, defaults.strength, 0, 1),
    colours,
    layer: oneOf(value.layer, COVER_EFFECT_LAYERS, defaults.layer),
  };
}

/**
 * A document from a client or from storage, checked and clamped: a number out of its range is
 * clamped, an unknown enum value gets its default, a text is cut. Null when the document cannot be
 * used at all: no object, another version, no valid background, a box or an effect that is not
 * valid, a list over its limit, or two items with the same id. Pure.
 */
export function parseCoverDoc(value: unknown): CoverDoc | null {
  if (!isRecord(value) || value.version !== COVER_DOC_VERSION) {
    return null;
  }
  const background = parseBackground(value.background);
  if (background === null || !Array.isArray(value.texts) || !Array.isArray(value.effects)) {
    return null;
  }
  if (value.texts.length > COVER_DOC_LIMITS.texts || value.effects.length > COVER_DOC_LIMITS.effects) {
    return null;
  }
  const texts = value.texts.map(parseText);
  const effects = value.effects.map(parseEffect);
  if (texts.some((text) => text === null) || effects.some((effect) => effect === null)) {
    return null;
  }
  const ids = [...texts, ...effects].map((item) => item?.id);
  if (new Set(ids).size !== ids.length) {
    return null;
  }
  const palette = Array.isArray(value.palette)
    ? value.palette
        .filter(isHexColour)
        .slice(0, COVER_DOC_LIMITS.palette)
        .map((colour) => colour.toLowerCase())
    : [];
  return {
    version: COVER_DOC_VERSION,
    background,
    texts: texts as CoverTextBox[],
    effects: effects as CoverEffect[],
    palette,
    themeId: oneOf(value.themeId, RELEASE_THEME_IDS, 'clean'),
  };
}

/** The length of a text in UTF-8 bytes. No `TextEncoder`: the file has no DOM or Node types. Pure. */
function utf8Length(text: string): number {
  let bytes = 0;
  for (const character of text) {
    const code = character.codePointAt(0) ?? 0;
    if (code < 0x80) {
      bytes += 1;
    } else if (code < 0x800) {
      bytes += 2;
    } else {
      bytes += code < 0x10000 ? 3 : 4;
    }
  }
  return bytes;
}

/** True when the JSON text of the document is inside the storage limit (UTF-8 bytes). */
export function isCoverDocSizeOk(doc: CoverDoc): boolean {
  return utf8Length(JSON.stringify(doc)) <= COVER_DOC_LIMITS.jsonBytes;
}

// ---------------------------------------------------------------------------------------------
// Defaults
// ---------------------------------------------------------------------------------------------

/** The one-click text layouts of the editor. `bottom` is the default of a new design. */
export const COVER_LAYOUTS = ['bottom', 'centre', 'corner', 'none'] as const;
export type CoverLayout = (typeof COVER_LAYOUTS)[number];

interface LayoutBox {
  x: number;
  y: number;
  width: number;
  sizeShare: number;
  align: CoverTextAlign;
}

const LAYOUT_BOXES: Readonly<Record<Exclude<CoverLayout, 'none'>, { title: LayoutBox; artist: LayoutBox }>> = {
  bottom: {
    title: { x: 0.5, y: 0.8, width: 0.84, sizeShare: 0.1, align: 'center' },
    artist: { x: 0.5, y: 0.9, width: 0.84, sizeShare: 0.038, align: 'center' },
  },
  centre: {
    title: { x: 0.5, y: 0.48, width: 0.86, sizeShare: 0.14, align: 'center' },
    artist: { x: 0.5, y: 0.62, width: 0.86, sizeShare: 0.042, align: 'center' },
  },
  corner: {
    title: { x: 0.36, y: 0.12, width: 0.6, sizeShare: 0.07, align: 'left' },
    artist: { x: 0.36, y: 0.19, width: 0.6, sizeShare: 0.032, align: 'left' },
  },
};

const LIGHT_TEXT = '#ffffff';
const DARK_TEXT = '#111111';

/**
 * The text colour of a new design: the lightest or the darkest colour of the palette when it reads
 * on the middle colour of the palette, else white or near-black. The soft shadow of the default
 * boxes keeps it readable on a picture. Pure.
 */
export function defaultCoverTextColour(palette: readonly string[]): string {
  const colours = palette.filter(isHexColour);
  if (colours.length === 0) {
    return LIGHT_TEXT;
  }
  const ground = colours[Math.floor(colours.length / 2)] ?? colours[0] ?? DARK_TEXT;
  const best = [...colours, LIGHT_TEXT, DARK_TEXT]
    .map((colour) => ({ colour, contrast: contrastRatio(colour, ground) }))
    .sort((a, b) => b.contrast - a.contrast)[0];
  return best !== undefined && best.contrast >= 4.5 ? best.colour.toLowerCase() : LIGHT_TEXT;
}

/** The case of a theme (`uppercase`, `lowercase`) as a box stores it. */
function caseOfTheme(value: string): CoverTextCase {
  if (value === 'uppercase') {
    return 'upper';
  }
  return value === 'lowercase' ? 'lower' : 'none';
}

/**
 * The title and the artist boxes of a layout, in the fonts, the case and the letter spacing of the
 * release theme. An empty title or artist gives no box. `none` gives no boxes. Pure.
 */
export function coverLayoutTexts(input: {
  layout: CoverLayout;
  themeId: ReleaseThemeId;
  palette: readonly string[];
  title: string;
  artist: string;
}): CoverTextBox[] {
  if (input.layout === 'none') {
    return [];
  }
  const type = themeType({ themeId: input.themeId, fontOverride: null });
  const colour = defaultCoverTextColour(input.palette);
  const shadowColour = contrastRatio(colour, DARK_TEXT) >= contrastRatio(colour, LIGHT_TEXT) ? '#000000' : '#ffffff';
  const boxes = LAYOUT_BOXES[input.layout];
  const make = (role: 'title' | 'artist', text: string): CoverTextBox | null => {
    const trimmed = text.trim().slice(0, COVER_DOC_LIMITS.textLength);
    if (trimmed === '') {
      return null;
    }
    const style = role === 'title' ? type.display : type.text;
    const fontId = role === 'title' ? type.fonts.display : type.fonts.text;
    const box = boxes[role];
    return {
      id: role,
      role,
      text: trimmed,
      fontId,
      weight: clampCoverFontWeight(fontId, style.weight),
      italic: style.italic,
      sizeShare: box.sizeShare,
      colour,
      opacity: role === 'title' ? 1 : 0.92,
      align: box.align,
      textCase: caseOfTheme(style.case),
      tracking: clamp(style.tracking, COVER_DOC_LIMITS.tracking[0], COVER_DOC_LIMITS.tracking[1]),
      lineHeight: role === 'title' ? 1.02 : 1.2,
      x: box.x,
      y: box.y,
      width: box.width,
      rotationDeg: 0,
      shadow: { colour: shadowColour, blurShare: 0.02, offsetXShare: 0, offsetYShare: 0.004 },
      outline: null,
      glow: null,
    };
  };
  return [make('title', input.title), make('artist', input.artist)].filter((box): box is CoverTextBox => box !== null);
}

/** A new design: the background, the boxes of the layout and no effects. Pure. */
export function defaultCoverDoc(input: {
  themeId: ReleaseThemeId;
  palette: readonly string[];
  title: string;
  artist: string;
  background: CoverBackground;
  layout?: CoverLayout;
}): CoverDoc {
  const palette = input.palette
    .filter(isHexColour)
    .slice(0, COVER_DOC_LIMITS.palette)
    .map((colour) => colour.toLowerCase());
  return {
    version: COVER_DOC_VERSION,
    background: input.background,
    texts: coverLayoutTexts({ ...input, palette, layout: input.layout ?? 'bottom' }),
    effects: [],
    palette,
    themeId: input.themeId,
  };
}

/** A new effect with the defaults of its type and colours from the palette. Pure. */
export function newCoverEffect(type: CoverEffectType, id: string, palette: readonly string[]): CoverEffect {
  const defaults = COVER_EFFECT_DEFAULTS[type];
  const colours = palette.filter(isHexColour);
  const fallback = ['#ff3ea5', '#6417fb'];
  return {
    id,
    type,
    strength: defaults.strength,
    colours: Array.from({ length: defaults.colourCount }, (_, index) =>
      (colours[colours.length - 1 - index] ?? fallback[index] ?? '#ffffff').toLowerCase()
    ),
    layer: defaults.layer,
  };
}

const normalised = (text: string): string => text.normalize('NFKC').replace(/\s+/g, ' ').trim().toLowerCase();

/**
 * True when a text box holds text that is not the title or the artist name of the song (owner
 * decision D9): stores can refuse such a cover, so the editor shows a note. Empty boxes do not
 * count. Pure.
 */
export function coverDocStoreWarning(doc: Pick<CoverDoc, 'texts'>, song: { title: string; artist: string }): boolean {
  const allowed = new Set([normalised(song.title), normalised(song.artist)].filter((text) => text !== ''));
  return doc.texts.some((box) => {
    const text = normalised(box.text);
    return text !== '' && !allowed.has(text);
  });
}
